import axios from 'axios'
import { API_HEADER_KEYS } from '../constants/httpConstants'
import {
  AUTH_STORAGE_KEYS,
  MAINTAIN_SESSION,
} from '../constants/user-session-constants'
import { runtimeConfig } from '../config/runtimeConfig'

const setHeader = (
  headers: Record<string, string> | undefined,
  key: string,
  value: string,
) => {
  if (!headers || !value) {
    return
  }

  headers[key] = value
}

/**
 * Reads the maintain session token from sessionStorage.
 *
 * @returns The token value when present, otherwise an empty string.
 */
const getMaintainSessionToken = (): string => {
  try {
    const tokenJson = sessionStorage.getItem(MAINTAIN_SESSION.STORAGE_KEY)
    if (!tokenJson) {
      return ''
    }

    const parsed = JSON.parse(tokenJson) as { token?: string }
    return parsed?.token || ''
  } catch {
    return ''
  }
}

/**
 * Registers the shared Axios request interceptor used by the app.
 *
 * The interceptor injects auth and user headers into every outgoing request.
 */
export const initializeHttpInterceptors = (): void => {
  axios.interceptors.request.use((config) => {
    const authToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) || ''
    const idToken = localStorage.getItem(AUTH_STORAGE_KEYS.ID_TOKEN) || ''
    const userId = localStorage.getItem(API_HEADER_KEYS.USER_ID) || ''

    const headers = (config.headers || {}) as Record<string, string>
    setHeader(headers, API_HEADER_KEYS.AUTHORIZATION, `${authToken}`)
    setHeader(headers, API_HEADER_KEYS.LPP_ID2_TOKEN, idToken)
    setHeader(headers, API_HEADER_KEYS.API_KEY, runtimeConfig.api.apiKey)
    setHeader(headers, API_HEADER_KEYS.USER_ID, userId)

    return {
      ...config,
      headers,
    }
  })
}

/**
 * Sends a GET request and returns the response payload.
 *
 * @typeParam T - The expected response body type.
 * @param url - The request URL.
 * @param config - Optional Axios request configuration.
 * @returns The parsed response body.
 */
export const httpGet = async <T>(url: string, config: any = {}): Promise<T> => {
  const response = await axios.get<T>(url, config)
  return response.data
}

/**
 * Sends a POST request and returns the response payload.
 *
 * @typeParam T - The expected response body type.
 * @param url - The request URL.
 * @param data - The request body payload.
 * @param config - Optional Axios request configuration.
 * @returns The parsed response body.
 */
export const httpPost = async <T>(
  url: string,
  data?: unknown,
  config: any = {},
): Promise<T> => {
  const response = await axios.post<T>(url, data, config)
  return response.data
}

/**
 * Sends a GET request with the maintain session token attached.
 *
 * @typeParam T - The expected response body type.
 * @param url - The request URL.
 * @param config - Optional Axios request configuration.
 * @returns The parsed response body.
 */
export const httpGetWithMaintainSessionToken = async <T>(
  url: string,
  config: any = {},
): Promise<T> => {
  const sessionToken = getMaintainSessionToken()

  return httpGet<T>(url, {
    ...config,
    headers: {
      ...(config.headers || {}),
      [API_HEADER_KEYS.LPP_ID2_TOKEN]: sessionToken,
    },
  })
}
